import React from 'react';
import styles from './ProgressChart.module.css';

/**
 * Props for the ProgressChart component
 */
interface ProgressChartProps {
  /** Skill data as a record of skill name to numeric score/level */
  skillData: Record<string, number>;
  /** Optional maximum level value for normalization (default: 10) */
  maxLevel?: number;
  /** Optional chart title (default: "Skill Progress") */
  title?: string;
}

/**
 * ProgressChart component
 * 
 * Displays a radar chart showing skill levels
 */
const ProgressChart: React.FC<ProgressChartProps> = ({ 
  skillData, 
  maxLevel = 10,
  title = "Skill Progress" 
}) => {
  // Filter out non-numeric values and get skill names
  const skills = Object.keys(skillData).filter(
    skill => typeof skillData[skill] === 'number' && !isNaN(skillData[skill])
  );
  
  // Calculate percentages for each skill
  const skillPercentages = skills.map(skill => {
    const level = skillData[skill];
    return (level / maxLevel) * 100;
  });
  
  // Calculate polygon points for the radar chart
  const calculatePolygonPoints = () => {
    const centerX = 100;
    const centerY = 100;
    const radius = 80;
    
    return skills.map((_, index) => {
      // Calculate the angle for this point (evenly distributed around the circle)
      const angle = (Math.PI * 2 * index) / skills.length - Math.PI / 2;
      
      // Calculate the distance from center based on skill level percentage
      const distance = (radius * skillPercentages[index]) / 100;
      
      // Calculate x and y coordinates
      const x = centerX + distance * Math.cos(angle);
      const y = centerY + distance * Math.sin(angle);
      
      return `${x},${y}`;
    }).join(' ');
  };

  const clampPercent = (value: number, min: number, max: number) => {
    return Math.min(max, Math.max(min, value));
  };
  
  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{title}</h2>
      
      <div className={styles.chartContainer}>
        <svg viewBox="0 0 200 200" className={styles.chart}>
          {/* Background grid circles */}
          {[20, 40, 60, 80, 100].map((percent, i) => (
            <circle
              key={`circle-${i}`}
              cx="100"
              cy="100"
              r={(80 * percent) / 100}
              className={styles.gridCircle}
            />
          ))}
          
          {/* Axes for each skill */}
          {skills.map((_, index) => {
            const angle = (Math.PI * 2 * index) / skills.length - Math.PI / 2;
            const x2 = 100 + 80 * Math.cos(angle);
            const y2 = 100 + 80 * Math.sin(angle);
            
            return (
              <line
                key={`axis-${index}`}
                x1="100"
                y1="100"
                x2={x2}
                y2={y2}
                className={styles.axis}
              />
            );
          })}
          
          {/* Only render polygon if we have skills */}
          {skills.length > 0 && (
            <polygon
              points={calculatePolygonPoints()}
              className={styles.dataPolygon}
            />
          )}
          
          {/* Data points */}
          {skills.map((_, index) => {
            const angle = (Math.PI * 2 * index) / skills.length - Math.PI / 2;
            const distance = (80 * skillPercentages[index]) / 100;
            const x = 100 + distance * Math.cos(angle);
            const y = 100 + distance * Math.sin(angle);
            
            return (
              <circle
                key={`point-${index}`}
                cx={x}
                cy={y}
                r="4"
                className={styles.dataPoint}
              />
            );
          })}
        </svg>
        
        {/* Skill labels */}
        <div className={styles.skillLabels}>
          {skills.map((skill, index) => {
            const angle = (Math.PI * 2 * index) / skills.length - Math.PI / 2;
            const x = 100 + 95 * Math.cos(angle);
            const y = 100 + 95 * Math.sin(angle);
            
            const xPercent = x / 2;
            const yPercent = y / 2;

            // Keep labels inside the chart bounds so detailed distributions don't overflow.
            const labelStyle = {
              left: `${clampPercent(xPercent, 12, 88)}%`,
              top: `${clampPercent(yPercent, 12, 88)}%`,
              transform: 'translate(-50%, -50%)'
            };
            
            // Format skill name for display (convert camelCase to Title Case)
            const displayName = skill
              .replace(/([A-Z])/g, ' $1')
              .replace(/^./, str => str.toUpperCase());
            
            return (
              <div 
                key={`label-${skill}`} 
                className={styles.skillLabel}
                style={labelStyle}
              >
                {displayName}
                <span className={styles.skillLevelBadge}>
                  {skillData[skill]}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div className={styles.legendColorBox}></div>
          <div className={styles.legendText}>Current Skills</div>
        </div>
        <div className={styles.legendItem}>
          <div className={styles.legendGrid}></div>
          <div className={styles.legendText}>Max Level ({maxLevel})</div>
        </div>
      </div>
    </div>
  );
};

export default ProgressChart; 
